import { cn } from '@shared/utils';

/*
 * 頂端橫幅的共用外觀。
 *
 * **內容各 app 自己組**（diet 放日期導覽，square-photo 放隨流程換句話的標題），
 * 但「這條橫幅長什麼樣」必須一致：背景、模糊、下緣線、對齊線與圖示鍵的觸控目標。
 *
 * ⚠️ 不要改用 `IonToolbar` 換掉這一組。iOS mode 的 `ion-title` 是置中的、MD mode
 * 靠左，同一份程式碼在兩個平台會長不一樣；而本專案刻意不鎖 mode（見
 * `@mobile/theme` 的說明），差異一律在樣式層對齊。`IonToolbar` 唯一多給的是
 * safe-area 內距，那一件事下面的 `APP_HEADER_CLASS` 自己做了。
 */

/**
 * 橫幅本體。放在 `IonHeader` 之內（`IonHeader` 只負責「不在捲動容器裡」）。
 *
 * **背景與模糊是必要的，不是裝飾。**`@mobile/theme` 把 `ion-content` 設成
 * 透明以透出底下的裝飾層，捲動內容因此會直接跑到橫幅底下——沒有這層底色，
 * 標題會與捲過去的內容疊在一起。下緣線則界定它與內容的邊界。
 *
 * 瀏海讓位用 Ionic 的 `--ion-safe-area-top` 而非裸的 `env(safe-area-inset-top)`：
 * iOS 加到主畫面的 standalone 模式下，`env()` 有時回報 0 但狀態列仍蓋住內容，
 * Ionic 對該情況備有 `--ion-statusbar-padding: 20px` 的 fallback（見 core.css 的
 * `plt-pwa`）。`IonHeader` 本身不處理 safe-area——做這件事的是 `IonToolbar`，
 * 而這裡刻意不用它。
 *
 * ⚠️ `backdrop-filter` 會為子孫的 `position: fixed` 建立包含區塊，`IonModal`
 * 一類的 overlay **不可以**放在這個元素之內，否則會被壓縮在橫幅那一條的範圍裡
 * 只露出一小片。面板一律與 header 並列，掛在 `IonPage` 底下。
 */
export const APP_HEADER_CLASS = cn(
  'border-b border-border',
  'bg-background/80 backdrop-blur-md',
  'pt-[var(--ion-safe-area-top,0px)]',
);

/**
 * 橫幅內部的容器：寬度與內距和主內容一致，橫幅裡的東西才會與下方卡片切齊同一條
 * 對齊線。高度由內容決定，而 44px 的圖示鍵讓兩個 app 的橫幅自然等高。
 */
export const APP_HEADER_INNER_CLASS = cn(
  'mx-auto flex max-w-5xl items-center justify-between gap-3',
  'px-4 py-2 lg:px-6',
);

/**
 * 橫幅上的圖示鍵。
 *
 * 44px 是觸控目標的下限，而非圖示的尺寸——圖示本身小得多，靠內距把可按範圍撐開。
 * 用它的地方通常要補一個 `-ml-2.5` / `-mr-2.5`，把內距造成的視覺內縮拉回對齊線上。
 */
export const ICON_BUTTON_CLASS = cn(
  'flex h-11 w-11 items-center justify-center rounded-md',
  'text-muted-foreground transition-all duration-200 ease-out',
  'hover:bg-foreground/5 hover:text-foreground active:scale-[0.98]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  'disabled:pointer-events-none disabled:opacity-40',
);
